import React from 'react';

const SECTIONS = [
  {
    icon: '⊹',
    title: 'Overview',
    content: `The Scatter Plot visualizes the relationship between Percent Identity (X-axis) and Alignment Length (Y-axis, displayed on a Log10 scale) for all ReadHit records. Each point represents a single BLAST hit. Points are colour-coded by group — either Polymerase Type (PV1, PV2, PV3) or Sequence ID Prefix (e.g. VP1_PV2) depending on data diversity.`,
  },
  {
    icon: '📐',
    title: 'Axes',
    rows: [
      ['X — % Identity',         'Percentage of aligned positions that are identical (0–100%). Higher values indicate closer matches to the reference.'],
      ['Y — Length (Log10)',      'Log base-10 of the raw alignment length in base pairs. Log scale compresses large ranges and reveals patterns across orders of magnitude.'],
    ],
  },
  {
    icon: '🎨',
    title: 'Color Groups',
    content: `Points are grouped and coloured automatically. If multiple Polymerase Types (PV1, PV2, PV3) are present in the data, grouping is by poltype. If only one poltype is present, grouping falls back to the two-part Sequence ID Prefix (e.g. VP1_PV2, 3D_PV1). The legend at the top-right shows group names and point counts.`,
  },
  {
    icon: '🔍',
    title: 'Filter By',
    content: `The "Filter By" dropdown lets you restrict the scatter plot to a single Polymerase Type or Sequence ID Prefix. Select "All Types" (default) to show all groups simultaneously. Filtering updates the chart instantly without reloading data.`,
  },
  {
    icon: '⚙️',
    title: 'Axis Filters',
    rows: [
      ['Enable Filters',       'Toggle the axis range filters on or off. Enabled by default with conservative defaults for performance.'],
      ['% Identity Min/Max',   'Restrict visible points to a specific percent identity range. Default: 75–101%.'],
      ['Align Length Min/Max', 'Restrict visible points by original alignment length in bp (not Log10). Default min: 2 bp, no upper limit.'],
    ],
  },
  {
    icon: '📏',
    title: 'Reference Lines & Areas',
    content: `When all four axis filter values are set, a semi-transparent red reference rectangle highlights the filtered region. Individual red dashed lines mark each axis boundary. These are visual guides only and do not affect the underlying data.`,
  },
  {
    icon: '📊',
    title: 'Data Summary',
    rows: [
      ['Total Points', 'Number of data points currently visible after filtering.'],
      ['Groups',       'Number of distinct colour groups rendered in the chart.'],
    ],
  },
  {
    icon: '⚠️',
    title: 'Performance',
    content: `The chart limits processing to the first 50,000 rows. A warning banner appears when this limit is reached. The default axis filters (75–101% identity, ≥ 2 bp length) are set deliberately to keep initial render times low. Widen the ranges gradually when needed.`,
  },
  {
    icon: '↺',
    title: 'Reset',
    content: `Click Reset to restore default values: Filter By = All Types, axis filters enabled, % identity 75–101, alignment length ≥ 2 bp with no upper limit.`,
  },
  {
    icon: '💡',
    title: 'Interpretation Tips',
    rows: [
      ['Cluster top-right',   'High identity + long alignment → strong, reliable hits to the reference.'],
      ['Cluster bottom-left', 'Low identity + short alignment → weak or spurious hits; check evalue.'],
      ['Vertical spread',     'Same identity but varying lengths → multiple reference regions matched at similar similarity.'],
      ['Horizontal spread',   'Same length but varying identity → hits of similar size but different quality.'],
      ['Isolated outliers',   'Points far from the main cluster may represent novel variants or contamination.'],
    ],
  },
];

export default function UserGuidScatterPlot({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="rh-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal-box rh-guide-modal" role="dialog" aria-modal="true" aria-labelledby="guide-title-scatter">

        <div className="rh-modal-header">
          <div>
            <div className="rh-modal-title" id="guide-title-scatter">Scatter Plot — User Guide</div>
            <div className="rh-modal-subtitle">% Identity vs Alignment Length (Log10)</div>
          </div>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="rh-guide-body">
          {SECTIONS.map((sec, i) => (
            <div key={i} className="rh-guide-section">
              <div className="rh-guide-section-title">
                <span style={{ fontSize: 14, marginRight: 4 }}>{sec.icon}</span>
                {sec.title}
              </div>

              {sec.content && <p className="rh-guide-text">{sec.content}</p>}

              {sec.rows && (
                <table className="rh-guide-table">
                  <tbody>
                    {sec.rows.map(([term, desc]) => (
                      <tr key={term}>
                        <td className="rh-guide-term">{term}</td>
                        <td className="rh-guide-desc">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
